import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DollarSign, Plane, Home, UtensilsCrossed, Ticket, Gift, Users, Calculator, Info } from 'lucide-react';

export default function TripBudgetCalculator({ 
  initialBudget = null, 
  tripDuration = 1,
  onBudgetUpdate,
  numberOfPeople = 1,
  accommodationType = 'hotel'
}) {
  const [budget, setBudget] = useState(() => ({
    transportation: {
      flights_per_person: initialBudget?.transportation?.flights_per_person || 0,
      baggage_per_person: initialBudget?.transportation?.baggage_per_person || 0,
      ground_transport_total: initialBudget?.transportation?.ground_transport_total || 0,
      other: initialBudget?.transportation?.other || 0
    },
    accommodations: {
      type: initialBudget?.accommodations?.type || accommodationType,
      nights: initialBudget?.accommodations?.nights || tripDuration,
      price_per_night: initialBudget?.accommodations?.price_per_night || 0,
      rooms_needed: initialBudget?.accommodations?.rooms_needed || 1,
      total_property_cost: initialBudget?.accommodations?.total_property_cost || 0
    },
    food: {
      daily_per_person: initialBudget?.food?.daily_per_person || 75,
      special_meals_total: initialBudget?.food?.special_meals_total || 0
    },
    activities: {
      items: initialBudget?.activities?.items || []
    },
    miscellaneous: {
      per_person: initialBudget?.miscellaneous?.per_person || 50
    }
  }));
  
  // Use ref to track if we've initialized from props
  const hasInitialized = useRef(false);
  
  // Initialize with provided budget only once
  useEffect(() => {
    if (initialBudget && !hasInitialized.current) {
      hasInitialized.current = true;
      setBudget({
        transportation: {
          flights_per_person: initialBudget.transportation?.flights_per_person || 0,
          baggage_per_person: initialBudget.transportation?.baggage_per_person || 0,
          ground_transport_total: initialBudget.transportation?.ground_transport_total || 0,
          other: initialBudget.transportation?.other || 0
        },
        accommodations: {
          type: initialBudget.accommodations?.type || accommodationType,
          nights: initialBudget.accommodations?.nights || tripDuration,
          price_per_night: initialBudget.accommodations?.price_per_night || 0,
          rooms_needed: initialBudget.accommodations?.rooms_needed || 1,
          total_property_cost: initialBudget.accommodations?.total_property_cost || 0
        },
        food: {
          daily_per_person: initialBudget.food?.daily_per_person || 75,
          special_meals_total: initialBudget.food?.special_meals_total || 0
        },
        activities: {
          items: initialBudget.activities?.items || []
        },
        miscellaneous: {
          per_person: initialBudget.miscellaneous?.per_person || 50
        }
      });
    }
  }, []); // Only run once on mount
  
  // Calculate totals - memoized to prevent recreation
  const calculateTotals = useCallback(() => {
    let perPersonTotal = 0;
    let sharedTotal = 0;
    
    // Transportation (per person)
    perPersonTotal += Number(budget.transportation.flights_per_person || 0);
    perPersonTotal += Number(budget.transportation.baggage_per_person || 0);
    sharedTotal += Number(budget.transportation.ground_transport_total || 0);
    sharedTotal += Number(budget.transportation.other || 0);
    
    // Accommodations (depends on type)
    const nights = Number(budget.accommodations.nights || tripDuration);
    if (budget.accommodations.type === 'hotel') {
      const roomCost = Number(budget.accommodations.price_per_night || 0) * 
                       Number(budget.accommodations.rooms_needed || 1) * nights;
      sharedTotal += roomCost;
    } else {
      // Airbnb/VRBO/Camping - whole property
      const propertyCost = Number(budget.accommodations.total_property_cost || 0) * nights;
      sharedTotal += propertyCost;
    }
    
    // Food (per person) - now includes 20% tip automatically
    const foodPerPerson = Number(budget.food.daily_per_person || 0) * nights;
    const foodTipPerPerson = foodPerPerson * 0.20; // 20% tip
    perPersonTotal += foodPerPerson + foodTipPerPerson;
    
    // Special meals (shared) - also includes 20% tip
    const specialMeals = Number(budget.food.special_meals_total || 0);
    const specialMealsTip = specialMeals * 0.20;
    sharedTotal += specialMeals + specialMealsTip;
    
    // Activities
    budget.activities.items.forEach(item => {
      if (item.is_per_person) {
        perPersonTotal += Number(item.cost || 0);
      } else {
        sharedTotal += Number(item.cost || 0);
      }
    });
    
    // Miscellaneous (per person)
    perPersonTotal += Number(budget.miscellaneous.per_person || 0);
    
    // Calculate final per-person cost
    const sharedPerPerson = numberOfPeople > 0 ? sharedTotal / numberOfPeople : 0;
    const totalPerPerson = perPersonTotal + sharedPerPerson;
    const tripTotal = (perPersonTotal * numberOfPeople) + sharedTotal;
    
    // Calculate min/max range (min is current, max adds 15% buffer)
    const minPerPerson = Math.round(totalPerPerson);
    const maxPerPerson = Math.round(totalPerPerson * 1.15);
    const minTotal = Math.round(tripTotal);
    const maxTotal = Math.round(tripTotal * 1.15);
    
    return {
      per_person: minPerPerson,
      per_person_max: maxPerPerson,
      trip_total: minTotal,
      trip_total_max: maxTotal,
      shared_costs: Math.round(sharedTotal),
      individual_costs: Math.round(perPersonTotal),
      food_tip_total: Math.round((foodTipPerPerson * numberOfPeople) + specialMealsTip)
    };
  }, [budget, tripDuration, numberOfPeople]);
  
  // Update parent when budget changes - use timeout to debounce
  const updateTimeoutRef = useRef(null);
  useEffect(() => {
    if (onBudgetUpdate) {
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Set a new timeout to debounce updates
      updateTimeoutRef.current = setTimeout(() => {
        const totals = calculateTotals();
        onBudgetUpdate({
          ...budget,
          totals
        });
      }, 300); // 300ms debounce
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [budget, calculateTotals, onBudgetUpdate]);
  
  const totals = calculateTotals();
  
  // Add activity item
  const addActivity = () => {
    setBudget(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        items: [
          ...prev.activities.items,
          { name: '', cost: 0, is_per_person: true }
        ]
      }
    }));
  };
  
  // Update activity item
  const updateActivity = (index, field, value) => {
    setBudget(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        items: prev.activities.items.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };
  
  // Remove activity item
  const removeActivity = (index) => {
    setBudget(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        items: prev.activities.items.filter((_, i) => i !== index)
      }
    }));
  };
  
  return (
    <div className="space-y-6">
      {/* Budget Summary Card */}
      <div className="bg-gradient-to-r from-gold-400 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            Trip Budget Calculator
          </h3>
          <div className="text-sm bg-white/20 px-3 py-1 rounded-full">
            {numberOfPeople} {numberOfPeople === 1 ? 'person' : 'people'}
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-xs opacity-90">Per Person</div>
            <div className="text-xl font-bold">
              ${totals.per_person || 0}-${totals.per_person_max || 0}
            </div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-xs opacity-90">Trip Total</div>
            <div className="text-xl font-bold">
              ${totals.trip_total || 0}-${totals.trip_total_max || 0}
            </div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-xs opacity-90">Shared Costs</div>
            <div className="text-lg font-semibold">${totals.shared_costs || 0}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-xs opacity-90">Individual</div>
            <div className="text-lg font-semibold">${totals.individual_costs || 0}</div>
          </div>
        </div>
      </div>
      
      {/* Transportation Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Plane className="w-5 h-5 text-azure-600" />
          Transportation
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Flight tickets (per person)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.transportation.flights_per_person}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  transportation: { ...prev.transportation, flights_per_person: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="350"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Baggage fees (per person)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.transportation.baggage_per_person}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  transportation: { ...prev.transportation, baggage_per_person: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="60"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Ground transport (total)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.transportation.ground_transport_total}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  transportation: { ...prev.transportation, ground_transport_total: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="200"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Other transport costs (total)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.transportation.other}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  transportation: { ...prev.transportation, other: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="0"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-azure-50 rounded-lg">
          <div className="text-sm text-azure-900">
            Transportation subtotal: <strong>${
              (Number(budget.transportation.flights_per_person || 0) * numberOfPeople) +
              (Number(budget.transportation.baggage_per_person || 0) * numberOfPeople) +
              Number(budget.transportation.ground_transport_total || 0) +
              Number(budget.transportation.other || 0)
            }</strong> ({
              Number(budget.transportation.flights_per_person || 0) + 
              Number(budget.transportation.baggage_per_person || 0)
            }/person + ${
              Number(budget.transportation.ground_transport_total || 0) +
              Number(budget.transportation.other || 0)
            } shared)
          </div>
        </div>
      </div>
      
      {/* Accommodations Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Home className="w-5 h-5 text-emerald-600" />
          Accommodations
        </h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Accommodation Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['hotel', 'airbnb', 'vrbo', 'camping'].map(type => (
              <button
                key={type}
                onClick={() => setBudget(prev => ({
                  ...prev,
                  accommodations: { ...prev.accommodations, type }
                }))}
                className={`px-3 py-2 rounded-lg border capitalize transition-colors ${
                  budget.accommodations.type === type
                    ? 'bg-gold-500 text-white border-gold-500'
                    : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
                }`}
              >
                {type === 'airbnb' ? 'Airbnb' : type === 'vrbo' ? 'VRBO' : type}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Number of nights
            </label>
            <input
              type="number"
              value={budget.accommodations.nights}
              onChange={(e) => setBudget(prev => ({
                ...prev,
                accommodations: { ...prev.accommodations, nights: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
              placeholder="5"
            />
          </div>
          
          {budget.accommodations.type === 'hotel' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Price per night (per room)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="number"
                    value={budget.accommodations.price_per_night}
                    onChange={(e) => setBudget(prev => ({
                      ...prev,
                      accommodations: { ...prev.accommodations, price_per_night: e.target.value }
                    }))}
                    className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                    placeholder="150"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Number of rooms needed
                </label>
                <input
                  type="number"
                  value={budget.accommodations.rooms_needed}
                  onChange={(e) => setBudget(prev => ({
                    ...prev,
                    accommodations: { ...prev.accommodations, rooms_needed: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                  placeholder="2"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Total cost per night (whole property)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="number"
                  value={budget.accommodations.total_property_cost}
                  onChange={(e) => setBudget(prev => ({
                    ...prev,
                    accommodations: { ...prev.accommodations, total_property_cost: e.target.value }
                  }))}
                  className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                  placeholder="250"
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
          <div className="text-sm text-emerald-900">
            Accommodation subtotal: <strong>${
              budget.accommodations.type === 'hotel'
                ? Number(budget.accommodations.price_per_night || 0) * 
                  Number(budget.accommodations.rooms_needed || 1) * 
                  Number(budget.accommodations.nights || tripDuration)
                : Number(budget.accommodations.total_property_cost || 0) * 
                  Number(budget.accommodations.nights || tripDuration)
            }</strong> (${
              Math.round((budget.accommodations.type === 'hotel'
                ? Number(budget.accommodations.price_per_night || 0) * 
                  Number(budget.accommodations.rooms_needed || 1) * 
                  Number(budget.accommodations.nights || tripDuration)
                : Number(budget.accommodations.total_property_cost || 0) * 
                  Number(budget.accommodations.nights || tripDuration)) / numberOfPeople)
            }/person for {budget.accommodations.nights || tripDuration} nights)
          </div>
        </div>
      </div>
      
      {/* Food Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-orange-600" />
          Food & Dining
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Daily food budget (per person)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.food.daily_per_person}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  food: { ...prev.food, daily_per_person: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="75"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Special meals/dining (total)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="number"
                value={budget.food.special_meals_total}
                onChange={(e) => setBudget(prev => ({
                  ...prev,
                  food: { ...prev.food, special_meals_total: e.target.value }
                }))}
                className="w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                placeholder="200"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-3 space-y-2">
          <div className="p-3 bg-orange-50 rounded-lg">
            <div className="text-sm text-orange-900">
              Food subtotal: <strong>${
                (Number(budget.food.daily_per_person || 0) * numberOfPeople * Number(budget.accommodations.nights || tripDuration)) +
                Number(budget.food.special_meals_total || 0)
              }</strong>
            </div>
            <div className="text-xs text-orange-700 mt-1">
              (${Number(budget.food.daily_per_person || 0) * Number(budget.accommodations.nights || tripDuration)}/person + 
              ${budget.food.special_meals_total || 0} special meals)
            </div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg">
            <div className="text-sm text-emerald-900">
              <span className="font-medium">+ 20% Tip included:</span> <strong>${totals.food_tip_total || 0}</strong>
            </div>
            <div className="text-xs text-emerald-700">
              Automatically added to all food expenses
            </div>
          </div>
          <div className="p-3 bg-orange-100 rounded-lg">
            <div className="text-sm font-semibold text-orange-900">
              Food total with tip: <strong>${
                Math.round(
                  ((Number(budget.food.daily_per_person || 0) * numberOfPeople * Number(budget.accommodations.nights || tripDuration)) +
                  Number(budget.food.special_meals_total || 0)) * 1.20
                )
              }</strong>
            </div>
          </div>
        </div>
      </div>
      
      {/* Activities Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-stone-900 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-violet-600" />
            Activities & Attractions
          </h4>
          <button
            onClick={addActivity}
            className="px-3 py-1 bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition-colors text-sm"
          >
            + Add Activity
          </button>
        </div>
        
        {budget.activities.items.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            No activities added yet. Click "Add Activity" to start.
          </div>
        ) : (
          <div className="space-y-3">
            {budget.activities.items.map((item, index) => (
              <div key={index} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateActivity(index, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                  placeholder="Activity name"
                />
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="number"
                    value={item.cost}
                    onChange={(e) => updateActivity(index, 'cost', e.target.value)}
                    className="w-32 pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                    placeholder="50"
                  />
                </div>
                <select
                  value={item.is_per_person ? 'person' : 'total'}
                  onChange={(e) => updateActivity(index, 'is_per_person', e.target.value === 'person')}
                  className="px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
                >
                  <option value="person">Per person</option>
                  <option value="total">Total cost</option>
                </select>
                <button
                  onClick={() => removeActivity(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {budget.activities.items.length > 0 && (
          <div className="mt-3 p-3 bg-violet-50 rounded-lg">
            <div className="text-sm text-violet-900">
              Activities subtotal: <strong>${
                budget.activities.items.reduce((total, item) => {
                  const cost = Number(item.cost || 0);
                  return total + (item.is_per_person ? cost * numberOfPeople : cost);
                }, 0)
              }</strong>
            </div>
          </div>
        )}
      </div>
      
      {/* Miscellaneous Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-pink-600" />
          Miscellaneous
        </h4>
        
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">
            Souvenirs & other expenses (per person)
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="number"
              value={budget.miscellaneous.per_person}
              onChange={(e) => setBudget(prev => ({
                ...prev,
                miscellaneous: { per_person: e.target.value }
              }))}
              className="w-full md:w-1/2 pl-10 pr-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500"
              placeholder="50"
            />
          </div>
        </div>
        
        <div className="mt-3 p-3 bg-pink-50 rounded-lg">
          <div className="text-sm text-pink-900">
            Miscellaneous subtotal: <strong>${
              Number(budget.miscellaneous.per_person || 0) * numberOfPeople
            }</strong> (${budget.miscellaneous.per_person || 0}/person)
          </div>
        </div>
      </div>
      
      {/* Tips Section */}
      <div className="bg-azure-50 border border-azure-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-azure-900">
            <p className="font-semibold mb-1">Budget Tips:</p>
            <ul className="space-y-1">
              <li>• Add 10-15% buffer for unexpected expenses</li>
              <li>• Consider travel insurance (typically 4-8% of trip cost)</li>
              <li>• Check if activities include taxes and fees</li>
              <li>• Factor in currency exchange rates for international trips</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}